import { useState, useMemo, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchInvoice, updateInvoiceFields, updateInvoiceExtraPayments } from '../api/invoices.js';
import { usePacksByLead, useCreatePack } from '../api/packs.js';
import { useLeadAccounts } from '../api/leads.js';
import { useToast } from '../contexts/ToastContext';
import { paymentBasisLabel } from '../lib/paymentBasis';
import { APPS } from '../constants/apps.js';
import { NumberStepper } from './NumberStepper.jsx';
import { queryKeys } from '../lib/queryKeys';
import InvoiceExtrasEditor, {
  fromApiExtras, toApiExtras, extrasTotalCents, extrasIssues, centsOf,
} from './InvoiceExtrasEditor.jsx';
import DocForm from './DocForm.jsx';
import { packDisplayName } from '../lib/packName.js';

/**
 * Phase 100 → Phase 101 W3: Edit draft invoice modal — Touchpoint 2 of D-11.
 *
 * Phase 100 added account + videoCount editing.
 * Phase 101 W3 replaces the account dropdown with a Pack selector (D-04a):
 *   - 0 packs → CTA «Создать пачку» (useCreatePack)
 *   - 1+ packs → select {value: pack.id, label: 'label · @handle'}
 *   - Submit disabled if contract.status !== 'COMPLETED' (D-04a guard)
 *   - Body: {packId, videoCount, cost, bonusVideoCount} instead of {accountId}
 *
 * INVOICE-FORM-UX: rendered through DocForm (doc-form.css), in flow, in the «Инвойсы» slot of the
 * contract that owns the draft — ContractCard is the only caller, keyed by the invoice id.
 */
export default function EditDraftInvoiceModal({ invoice, leadId, contractStatus: contractStatusProp, onClose, onSaved }) {
  // Phase 101 W3: Pack selector (replaces account dropdown)
  const resolvedLeadId = leadId ?? invoice?.contract?.lead?.id ?? invoice?.leadId;
  const [selectedPackId, setSelectedPackId] = useState(invoice?.packId ?? null);
  const [videoCount, setVideoCount] = useState(
    invoice?.videoCount == null ? '' : String(invoice.videoCount),
  );
  const [bonusVideoCount, setBonusVideoCount] = useState(
    invoice?.bonusVideoCount == null ? '' : String(invoice.bonusVideoCount),
  );
  // Invoice kind snapshot (test|ambassador) — frozen from the lead's status at
  // creation, editable here to correct a wrong snapshot (drives the chip on the card/list).
  const [kind, setKind] = useState(invoice?.kind || 'ambassador');

  const qc = useQueryClient();
  const { addToast } = useToast();

  // D5 / trap 1: the InvoicesView host passes row.invoice, and P-E1 hangs the extras on the ROW.
  // gcTime: 0 — the entry is dropped when the modal unmounts, so every open starts from data:
  // undefined and the seed can only come from THIS open's GET. The client default (queryClient.js:21-22
  // — staleTime 30s / gcTime 5min) serves the previous open's snapshot on the first render: measured
  // against @tanstack/query-core 5.91.2, an untouched Save then PATCHes that snapshot back over a set
  // another role changed. staleTime: 0 and refetchOnMount: 'always' do NOT prevent it (measured).
  const { data: fresh, isLoading, isError } = useQuery({
    queryKey: queryKeys.invoices.detail(invoice.id),
    queryFn: () => fetchInvoice(invoice.id),
    gcTime: 0,
  });

  // A1 + A-R2-1: `null` = «not loaded yet» (an empty object would claim «no extras»). Seeds ONCE from
  // THIS open's GET; the guard is initialRef, not the setter, so form and compare baseline pin together.
  const [extras, setExtras] = useState(null);
  const initialRef = useRef(null);            // canonical set this form was seeded from
  useEffect(() => {
    if (!fresh || initialRef.current !== null) return;
    initialRef.current = toApiExtras(fromApiExtras(fresh.extraPayments ?? []));
    setExtras(fromApiExtras(fresh.extraPayments ?? []));
  }, [fresh]);

  // Phase 101 W3: pack list for this lead
  const { data: packs = [], isLoading: packsLoading } = usePacksByLead(resolvedLeadId);
  const createPackMut = useCreatePack();

  // Phase 101 W6 hotfix: pack creation now lets manager choose which lead
  // accounts go into the new pack. Tri-state UI: collapsed CTA → expanded panel
  // with checkboxes → mutation.
  const [createPackOpen, setCreatePackOpen] = useState(false);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  // PACK-APP-REQ: the new pack's application — required, no default.
  const [newPackApp, setNewPackApp] = useState('');
  const leadAccountsQuery = useLeadAccounts(createPackOpen ? resolvedLeadId : null);
  const allLeadAccounts = leadAccountsQuery.data?.accounts ?? [];
  // Only unassigned accounts (packId IS NULL) can be added to a new pack.
  const unassignedAccounts = useMemo(
    () => allLeadAccounts.filter((a) => !a.packId),
    [allLeadAccounts],
  );

  // D-04a guard: contract must be COMPLETED to submit.
  // Prefer the explicit prop (ContractCard/InvoicesView already know the contract);
  // fall back to a nested invoice.contract only if no prop is supplied.
  const contractStatus = contractStatusProp ?? invoice?.contract?.status ?? invoice?.contractStatus;
  const contractCompleted = contractStatus === 'COMPLETED' || contractStatus === 'completed';

  const mut = useMutation({
    mutationFn: async () => {
      // D6: extras first — they are the money; if that write fails nothing else has moved. A2/A-R2-1:
      // BOTH sides go through toApiExtras and the baseline is the SEED snapshot (initialRef), never
      // live `fresh`, so an untouched form sends no PATCH and a concurrent change cannot cause a revert.
      const initial = initialRef.current ?? [];
      const next    = extras === null ? initial : toApiExtras(extras);
      const extrasChanged = fresh?.status === 'draft'
        && JSON.stringify(next) !== JSON.stringify(initial);
      if (extrasChanged) await updateInvoiceExtraPayments(invoice.id, next);

      const patch = {};
      if (selectedPackId !== (invoice?.packId ?? null)) {
        patch.packId = selectedPackId;
      }
      const currentVc = invoice?.videoCount == null ? '' : String(invoice.videoCount);
      if (currentVc !== videoCount) {
        patch.videoCount = videoCount === '' ? null : Number(videoCount);
      }
      const currentBvc = invoice?.bonusVideoCount == null ? '' : String(invoice.bonusVideoCount);
      if (currentBvc !== bonusVideoCount) {
        // NOT NULL column — clearing the field means 0.
        patch.bonusVideoCount = bonusVideoCount === '' ? 0 : Number(bonusVideoCount);
      }
      if ((invoice?.kind || 'ambassador') !== kind) {
        patch.kind = kind;
      }
      if (Object.keys(patch).length === 0) return invoice;
      return updateInvoiceFields(invoice.id, patch).catch((err) => {
        if (extrasChanged) err.extrasSaved = true;   // D6: name the step that failed in the toast
        throw err;
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      // D12: the open-modal case — the detail entry does not survive the modal (gcTime: 0).
      qc.invalidateQueries({ queryKey: queryKeys.invoices.detail(invoice.id) });
      addToast('Сохранено', 'Изменения применены', 'success');
      onSaved?.();
      onClose();
    },
    onError: (err) => {
      // D9: plain Error (.message/.status/.data), no `.response`; EXTRAS_FROZEN's own `error` text
      // (routes/invoices.js:1304) IS the sentence the user must see, so no per-code branch is needed.
      const msg = err?.extrasSaved
        ? 'Допвыплаты сохранены, но остальные поля — нет'
        : err?.data?.error || err?.message || 'Не удалось сохранить';
      addToast('Ошибка', msg, 'error');
    },
  });

  const vcValid =
    videoCount === '' ||
    (Number.isInteger(Number(videoCount)) && Number(videoCount) >= 0);
  const bvcValid =
    bonusVideoCount === '' ||
    (Number.isInteger(Number(bonusVideoCount)) && Number(bonusVideoCount) >= 0);

  // D5 / A-R2-4: the same arithmetic and the same «—» rule as the create form's total (D4).
  const editorTotal = extras === null || extrasIssues(extras).length > 0 ? '—' : `USD ${((centsOf(fresh?.baseFee) + extrasTotalCents(extras)) / 100).toFixed(2)}`;

  // D-04a: submit gated on COMPLETED contract + selectedPackId (pack required for new invoices).
  // A3/A-R2-2 APPEND four clauses and remove none (owner ruling LEDGER 341, option A).
  const canSubmit = contractCompleted && vcValid && bvcValid && !mut.isPending
    && extras !== null && fresh?.status === 'draft' && !isError && extrasIssues(extras).length === 0;
  const submitTooltip = fresh && fresh.status !== 'draft'
    ? 'Допвыплаты правятся только в черновике'
    : !contractCompleted
    ? 'Договор должен быть в статусе COMPLETED для привязки пачки'
    : !vcValid
      ? 'Укажите корректное количество видео'
      : !bvcValid
        ? 'Укажите корректное количество бонусов'
        : undefined;

  const packLabel = packDisplayName;

  return (
    <DocForm
      title={`Редактировать черновик${invoice?.invoiceNo ? ` · ${invoice.invoiceNo}` : ''}`}
      meta={`Тип оплаты: ${paymentBasisLabel(invoice?.paymentBasis)}`}
      onClose={onClose}
      closeDisabled={mut.isPending}
      actions={(
        <>
          <button
            type="button"
            className="documents-action-btn"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Отмена
          </button>
          <button
            type="button"
            className="documents-action-btn documents-action-btn--primary"
            onClick={() => mut.mutate()}
            disabled={!canSubmit}
            title={submitTooltip}
          >
            {mut.isPending ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      )}
    >
      <div className="doc-form__grid">
        {/* Phase 101 W3: Pack selector */}
        <div className="contract-inline-form__field doc-form__span2">
          <label>Пачка</label>
          {packsLoading ? (
            <p className="doc-form__muted" style={{ fontSize: 13 }}>Загрузка пачек…</p>
          ) : packs.length === 0 && !createPackOpen ? (
            <div>
              <p className="doc-form__muted">
                У этого блогера нет пачек.
              </p>
              <button
                type="button"
                className="documents-action-btn"
                onClick={() => setCreatePackOpen(true)}
              >
                Создать пачку
              </button>
            </div>
          ) : createPackOpen ? (
            <div className="pack-create-panel">
              <p className="pack-create-panel__hint">
                Выбери аккаунты, которые войдут в эту пачку. Один аккаунт может состоять только в одной пачке.
              </p>
              {leadAccountsQuery.isLoading ? (
                <p className="doc-form__muted">Загрузка аккаунтов…</p>
              ) : unassignedAccounts.length === 0 ? (
                <p className="doc-form__muted">
                  Все аккаунты лида уже привязаны к пачкам.
                </p>
              ) : (
                <ul className="pack-create-panel__list">
                  {unassignedAccounts.map((a) => (
                    <li key={a.id}>
                      <label className="pack-create-panel__row">
                        <input
                          type="checkbox"
                          checked={selectedAccountIds.includes(a.id)}
                          onChange={(e) => {
                            setSelectedAccountIds((prev) => (
                              e.target.checked
                                ? [...prev, a.id]
                                : prev.filter((id) => id !== a.id)
                            ));
                          }}
                        />
                        <span className="pack-create-panel__platform">{a.platform}</span>
                        <span className="pack-create-panel__handle">@{a.handle}</span>
                        {a.isPrimary && <span className="pack-create-panel__primary">primary</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="pack-create-panel__hint">Приложение пачки</p>
              <select
                className="pack-create-panel__app"
                aria-label="Приложение"
                value={newPackApp}
                onChange={(e) => setNewPackApp(e.target.value)}
              >
                <option value="">— Выберите приложение —</option>
                {APPS.map((a) => <option key={a.value} value={a.value}>{a.value}</option>)}
              </select>
              <div className="pack-create-panel__actions">
                <button
                  type="button"
                  className="documents-action-btn"
                  onClick={() => {
                    setCreatePackOpen(false);
                    setSelectedAccountIds([]);
                    setNewPackApp('');
                  }}
                  disabled={createPackMut.isPending}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="documents-action-btn documents-action-btn--primary"
                  disabled={createPackMut.isPending || selectedAccountIds.length === 0 || !newPackApp}
                  onClick={() => {
                    createPackMut.mutate(
                      { leadId: resolvedLeadId, accountIds: selectedAccountIds, application: newPackApp },
                      {
                        onSuccess: (data) => {
                          const newId = data?.pack?.id ?? data?.id;
                          if (newId) setSelectedPackId(newId);
                          addToast('Готово', `Пачка создана (${selectedAccountIds.length} акк.)`, 'success');
                          setCreatePackOpen(false);
                          setSelectedAccountIds([]);
                          setNewPackApp('');
                        },
                        onError: (err) => {
                          const code = err?.data?.error;
                          const msg = code === 'account_already_in_pack'
                            ? 'Один из аккаунтов уже в другой пачке — обнови страницу.'
                            : 'Не удалось создать пачку';
                          addToast('Ошибка', msg, 'error');
                        },
                      },
                    );
                  }}
                >
                  {createPackMut.isPending
                    ? 'Создание…'
                    : `Создать пачку (${selectedAccountIds.length})`}
                </button>
              </div>
            </div>
          ) : (
            <div className="pack-select-row">
              <select
                value={selectedPackId ?? ''}
                onChange={(e) => setSelectedPackId(e.target.value || null)}
              >
                <option value="">— Выберите пачку —</option>
                {packs.map((p) => (
                  <option key={p.id} value={p.id}>{packLabel(p)}</option>
                ))}
              </select>
              <button
                type="button"
                className="pack-select-row__add"
                onClick={() => setCreatePackOpen(true)}
                title="Создать ещё одну пачку"
              >
                + пачка
              </button>
            </div>
          )}
          {/* D-04a guard note */}
          {!contractCompleted && (
            <p className="doc-form__warn">
              Договор не COMPLETED — привязка пачки заблокирована
            </p>
          )}
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`edit-video-count-${invoice.id}`} title="Количество видео (шт.)">Количество видео (шт.)</label>
          <NumberStepper id={`edit-video-count-${invoice.id}`} value={videoCount} onChange={setVideoCount} min={0} ariaLabel="количество видео" />
        </div>

        <div className="contract-inline-form__field" title="количество, не сумма">
          <label htmlFor={`edit-bonus-count-${invoice.id}`}>Бонусные видео (шт.)</label>
          <NumberStepper id={`edit-bonus-count-${invoice.id}`} value={bonusVideoCount} onChange={setBonusVideoCount} min={0} ariaLabel="бонусные видео" />
        </div>

        <div className="contract-inline-form__field">
          <label htmlFor={`edit-kind-${invoice.id}`}>Тип блогера</label>
          <select
            id={`edit-kind-${invoice.id}`}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="test">Тестовый</option>
            <option value="ambassador">Постоянный</option>
          </select>
        </div>

        {/* The base is not editable here: read-only, from THIS open's GET («—» until it lands). */}
        <div className="contract-inline-form__field">
          <label>База</label>
          <div className="doc-form__value">{fresh ? `USD ${fresh.baseFee}` : '—'}</div>
        </div>
      </div>

      {/* D5/D6: draft only (LEDGER 322), seeded from THIS open's GET. The fifth state is deliberate:
          one paint with `fresh` loaded and `extras` still null renders nothing, Save dead (canSubmit). */}
      {isLoading ? (
        <p className="doc-form__muted">Загрузка…</p>
      ) : isError ? (
        <p className="doc-form__warn">Не удалось загрузить инвойс</p>
      ) : fresh && fresh.status !== 'draft' ? (
        <p className="doc-form__warn">Допвыплаты правятся только в черновике</p>
      ) : extras !== null ? (
        <>
          <InvoiceExtrasEditor value={extras} onChange={setExtras} />
          <div className="doc-form__strip">
            <span className="doc-form__strip-total">Итого по инвойсу: {editorTotal}</span>
          </div>
        </>
      ) : null}
    </DocForm>
  );
}
